import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Copy,
  FileText,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DuplicateDetectionPanel() {
  const { getAuthHeaders } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [selectedStudy]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = selectedStudy ? `?study_id=${selectedStudy}` : '';
      const response = await axios.get(`${API_URL}/api/documents/duplicates-report${params}`, {
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching duplicate report:', error);
      toast.error('Failed to fetch duplicate report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 border border-slate-300 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Total Documents</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{report?.total_documents || 0}</p>
        </div>
        
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 border border-slate-300 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Unique Documents</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{report?.unique_documents || 0}</p>
        </div>
        
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 border border-slate-300 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <Copy className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Duplicate Groups</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{report?.duplicate_groups || 0}</p>
        </div>
        
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 border border-slate-300 dark:border-slate-600">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Duplicate Files</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{report?.total_duplicate_files || 0}</p>
        </div>
      </div>

      {/* Duplicate Details */}
      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6 border border-slate-300 dark:border-slate-600">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-red-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Duplicate Document Groups</h3>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReport}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {!report?.duplicate_details || report.duplicate_details.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
            <p className="text-lg font-medium">No Duplicates Found</p>
            <p className="text-sm">All documents in the repository are unique</p>
          </div>
        ) : (
          <ScrollArea className="h-96">
            <div className="space-y-4">
              {report.duplicate_details.map((group, idx) => (
                <div key={idx} className="bg-slate-200 dark:bg-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">{group.count} duplicates</Badge>
                      <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                        Hash: {group.content_hash}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {group.documents.map((doc, docIdx) => (
                      <div key={docIdx} className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-900 dark:text-slate-100">{doc.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                          <span>{doc.uploaded_by_name}</span>
                          <span>•</span>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        {report?.generated_at && (
          <p className="text-xs text-slate-500 mt-4 text-right">
            Report generated: {new Date(report.generated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
